import { Dispatch, KeyboardEventHandler, MouseEventHandler, SetStateAction } from 'react';
import { parse as queryString } from 'query-string';
import { FormType } from './SingleDivisionContainer.types';
import { UseFormReturn } from 'react-hook-form';
import { ProcessEditionFormType } from '@features/processes/interfaces/process-edition.interface';
import { DivisionCliente, ProcesoDivision } from '@interfaces/firestore-documents.interfaces';
import { DivisionEditionFormType } from '@features/divisions/interfaces/division-edition.interface';
import ProcessCreationFormType from '@features/processes/interfaces/process-creation.interface';
import { DEFAULT_PROCESS_CREATION_DATA } from '@features/processes/constants/process-forms.constants';
import { FiltersStates, ProcessesFiltersForm } from '@features/filters';

export function handleBackButtonClick(
	divisionEditionForm: UseFormReturn<DivisionEditionFormType>,
	form: FormType,
	processEditionForm: UseFormReturn<ProcessEditionFormType>,
	setAfterSaveOnBeforeCloseDialogAction: Dispatch<SetStateAction<(() => void) | null>>,
	setBeforeCloseDialog: Dispatch<SetStateAction<boolean>>,
	setDivisionNumber: Dispatch<SetStateAction<string>>
): MouseEventHandler<HTMLButtonElement> {
	return function () {
		function realFn(): void {
			setDivisionNumber('');

			const { search: searchQuery, filter: filterQuery } = queryString(
				document.location.search
			);

			if (typeof searchQuery === 'string' && typeof filterQuery === 'string') {
				window.history.pushState(
					null,
					'',
					`${document.location.pathname}?search=${searchQuery}&filter=${filterQuery}`
				);
			}
		}

		if (
			(form === 'edite-division' && divisionEditionForm.formState.isDirty) ||
			(form === 'edite-process' && processEditionForm.formState.isDirty)
		) {
			setAfterSaveOnBeforeCloseDialogAction(function () {
				return realFn;
			});

			setBeforeCloseDialog(true);
			return;
		}

		realFn();
	};
}

export function searchProcesses(
	processes: ProcesoDivision[],
	filters: ProcessesFiltersForm,
	setProcesses: Dispatch<SetStateAction<ProcesoDivision[]>>
): void {
	const newData = [];
	for (const process of processes) {
		// Filtrar por estado del proceso.
		if (
			(filters.state === FiltersStates.Enabled && !process.estado) ||
			(filters.state === FiltersStates.Disabled && process.estado)
		) {
			continue;
		}

		// Filtrar por nombre o id proceso.
		if (
			filters.process.length > 0 &&
			!process.nombre_proceso.toLowerCase().includes(filters.process.toLowerCase()) &&
			!process.id_proceso_text.includes(filters.process)
		) {
			continue;
		}

		newData.push(process);
	}

	setProcesses(newData);
}

export function handleFiltersClearClick(
	processes: ProcesoDivision[],
	filters: ProcessesFiltersForm,
	setProcesses: Dispatch<SetStateAction<ProcesoDivision[]>>
): () => void {
	return function () {
		searchProcesses(processes, { ...filters, process: '' }, setProcesses);
	};
}

export function handleFiltersKeyPress(
	processes: ProcesoDivision[],
	filters: ProcessesFiltersForm,
	setProcesses: Dispatch<SetStateAction<ProcesoDivision[]>>
): KeyboardEventHandler<HTMLInputElement> {
	return function (e) {
		if (e.key === 'Enter') {
			searchProcesses(processes, filters, setProcesses);
		}
	};
}

export function handleDivisionDetailsClick(
	form: FormType,
	processEditionForm: UseFormReturn<ProcessEditionFormType>,
	setAfterSaveOnBeforeCloseDialogAction: Dispatch<SetStateAction<(() => void) | null>>,
	setBeforeCloseDialog: Dispatch<SetStateAction<boolean>>,
	setEditionProcessData: Dispatch<SetStateAction<ProcesoDivision | null>>,
	setForm: Dispatch<SetStateAction<FormType>>
): () => void {
	return function () {
		if (form === 'edite-process' && processEditionForm.formState.isDirty) {
			setAfterSaveOnBeforeCloseDialogAction(function () {
				return function () {
					setForm('edite-division');
				};
			});

			setBeforeCloseDialog(true);
			return;
		}

		setEditionProcessData(null);
		setForm('edite-division');
	};
}

export function handleAddProcessClick(
	divisionEditionForm: UseFormReturn<DivisionEditionFormType>,
	form: FormType,
	processEditionForm: UseFormReturn<ProcessEditionFormType>,
	setAfterSaveOnBeforeCloseDialogAction: Dispatch<SetStateAction<(() => void) | null>>,
	setBeforeCloseDialog: Dispatch<SetStateAction<boolean>>,
	setForm: Dispatch<SetStateAction<FormType>>
): () => void {
	return function () {
		if (
			(form === 'edite-process' && processEditionForm.formState.isDirty) ||
			(form === 'edite-division' && divisionEditionForm.formState.isDirty)
		) {
			setAfterSaveOnBeforeCloseDialogAction(function () {
				return function () {
					setForm('create-process');
				};
			});

			setBeforeCloseDialog(true);
			return;
		}

		setForm('create-process');
	};
}

export function handleProcessClick(
	divisionEditionForm: UseFormReturn<DivisionEditionFormType>,
	editionProcessData: ProcesoDivision | null,
	form: FormType,
	processEditionForm: UseFormReturn<ProcessEditionFormType>,
	setAfterSaveOnBeforeCloseDialogAction: Dispatch<SetStateAction<(() => void) | null>>,
	setBeforeCloseDialog: Dispatch<SetStateAction<boolean>>,
	setEditionProcessData: Dispatch<SetStateAction<ProcesoDivision | null>>,
	setForm: Dispatch<SetStateAction<FormType>>
): (processData: ProcesoDivision) => () => void {
	return function (processData) {
		return function () {
			if (
				form === 'edite-process' &&
				editionProcessData?.id_proceso === processData.id_proceso
			) {
				return;
			}

			if (
				(form === 'edite-process' && processEditionForm.formState.isDirty) ||
				(form === 'edite-division' && divisionEditionForm.formState.isDirty)
			) {
				setAfterSaveOnBeforeCloseDialogAction(function () {
					return function () {
						processEditionForm.reset({
							...processData,
							nombre_proceso: '',
							ranges: processData.ranges.map((r) => ({
								...r,
								cliente_genera_numeracion: false,
								consecutivo_actual: r.consecutivo_actual ?? r.consecutivo_desde,
							})),
						});

						setEditionProcessData(processData);
						setForm('edite-process');
					};
				});

				setBeforeCloseDialog(true);
				return;
			}
			setEditionProcessData(processData);
			setForm('edite-process');

			processEditionForm.reset({
				...processData,
				nombre_proceso: '',
				ranges: processData.ranges.map((r) => ({
					...r,
					cliente_genera_numeracion: false,
					consecutivo_actual: r.consecutivo_actual ?? r.consecutivo_desde,
				})),
			});
		};
	};
}

export function handleCancelEditionPanel(
	afterSaveOnBeforeCloseDialogAction: (() => void) | null,
	divisionData: DivisionCliente,
	divisionEditionForm: UseFormReturn<DivisionEditionFormType>,
	processCreationForm: UseFormReturn<ProcessCreationFormType>,
	processEditionForm: UseFormReturn<ProcessEditionFormType>,
	setAfterSaveOnBeforeCloseDialogAction: Dispatch<SetStateAction<(() => void) | null>>,
	setBeforeCloseDialog: Dispatch<SetStateAction<boolean>>,
	setBeforeSaveData: Dispatch<SetStateAction<ProcessEditionFormType | null>>,
	setBeforeSaveDialog: Dispatch<SetStateAction<string>>,
	setEditionProcessData: Dispatch<SetStateAction<ProcesoDivision | null>>,
	setForm: Dispatch<SetStateAction<FormType>>
): () => void {
	return function () {
		const rangeActual = divisionData.agw_ranges.find((range) => range.en_uso);
		divisionEditionForm.reset({
			agw_ranges: divisionData.agw_ranges.map((range) => ({ ...range })),
			combos: divisionData.combos.map((c) => c.id_combo).sort((a, b) => a - b),
			consecutivo_desde: rangeActual?.consecutivo_desde,
			consecutivo_hasta: rangeActual?.consecutivo_hasta,
			notificacion_remitente: divisionData.notificacion_remitente,
			prefijo_consecutivo: rangeActual?.prefijo_consecutivo,
		});

		processCreationForm.reset(DEFAULT_PROCESS_CREATION_DATA);
		processEditionForm.reset({});

		setBeforeCloseDialog(false);
		setBeforeSaveData(null);
		setBeforeSaveDialog('');
		setEditionProcessData(null);
		setForm('none');

		if (afterSaveOnBeforeCloseDialogAction) {
			afterSaveOnBeforeCloseDialogAction();
		}

		setAfterSaveOnBeforeCloseDialogAction(null);
	};
}

export function handleClosePanel(
	divisionData: DivisionCliente,
	divisionEditionForm: UseFormReturn<DivisionEditionFormType>,
	form: FormType,
	processCreationForm: UseFormReturn<ProcessCreationFormType>,
	processEditionForm: UseFormReturn<ProcessEditionFormType>,
	setAfterSaveOnBeforeCloseDialogAction: Dispatch<SetStateAction<(() => void) | null>>,
	setBeforeCloseDialog: Dispatch<SetStateAction<boolean>>,
	setBeforeSaveData: Dispatch<SetStateAction<ProcessEditionFormType | null>>,
	setBeforeSaveDialog: Dispatch<SetStateAction<string>>,
	setEditionProcessData: Dispatch<SetStateAction<ProcesoDivision | null>>,
	setForm: Dispatch<SetStateAction<FormType>>
): () => void {
	return function () {
		if (
			(form === 'create-process' &&
				Object.keys(processCreationForm.formState.dirtyFields).length > 0) ||
			(form === 'edite-division' && divisionEditionForm.formState.isDirty) ||
			(document.getElementById('edition-panel-actions') &&
				form === 'edite-process' &&
				processEditionForm.formState.isDirty)
		) {
			setBeforeCloseDialog(true);
			return;
		}

		handleCancelEditionPanel(
			null,
			divisionData,
			divisionEditionForm,
			processCreationForm,
			processEditionForm,
			setAfterSaveOnBeforeCloseDialogAction,
			setBeforeCloseDialog,
			setBeforeSaveData,
			setBeforeSaveDialog,
			setEditionProcessData,
			setForm
		)();
	};
}

export function handleBackBeforeCloseDialog(
	setBeforeCloseDialog: Dispatch<SetStateAction<boolean>>
): () => void {
	return function () {
		setBeforeCloseDialog(false);
	};
}

export function handleBackBeforeSaveDialog(
	setBeforeSaveData: Dispatch<SetStateAction<ProcessEditionFormType | null>>,
	setBeforeSaveDialog: Dispatch<SetStateAction<string>>
): () => void {
	return function () {
		setBeforeSaveDialog('');
		setBeforeSaveData(null);
	};
}
